import { deepMerge } from 'toolbox/deepMerge';
import ThirdPartyLoader from 'components/global/ThirdPartyLoader';
import { setHash } from 'toolbox/location';
import { Event } from 'services/EventEmitter';
import { trigger } from 'toolbox/event';

/**
 * This is a description of the BazaarVoiceHosted constructor function.
 * @class
 * @classdesc Loads bazaarvoice script and after sets up some necessary events
 * @extends ThirdPartyLoader
 */
export default class BazaarVoiceHosted extends ThirdPartyLoader {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            version: 'v1', // the default version of the BV library; can also be set to v2
        }, options));
    }

    /**
     * After init
     * Run any script after the component is fully initialized
     */
    afterInit() {
        Event.emit('BV.dependency.check');
    }

    /**
     * On BV dependent component load event handler, called when BV dependent component is loaded
     */
    onDependencyLoaded() {
        this.loadScript();
    }

    /**
     * On script load handler, called when the third-party is loaded
     * @param {Object} data - Response of the provider
     */
    onScriptLoaded(data) {
        const events = {
            submissionLoad: this.onSubmissionLoad.bind(this),
            submissionSubmitted: this.onSubmissionSubmitted.bind(this),
        };

        super.onScriptLoaded(data);

        trigger('BV.dependency.scriptloaded', this.element, { bubbles: true });

        if (typeof window.bvCallback !== 'function') {
            window.bvCallback = this.onBVCallback.bind(this);
        }

        if (this.options.version === 'v1' && typeof $BV !== 'undefined' && $BV !== 'null') {
            $BV.configure('global', { events });
        } else if (this.options.version === 'v2' && typeof BV !== 'undefined' && BV !== 'null') {
            BV.configure('global', { events });
        }
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.reviews = document.querySelector('[data-element-type="reviews"]');
        this.selectors.questions = document.querySelector('[data-element-type="questions"]');
        this.selectors.reviewsTab = document.querySelector('[data-titlehash="reviews"]');
        this.selectors.questionsTab = document.querySelector('[data-titlehash="questions-answers"]');
        this.reviewsTabHash = this.selectors.reviews ? this.selectors.reviews.parentElement.getAttribute('data-tab-hash') : '';
        this.questionsTabHash = this.selectors.questions ? this.selectors.questions.parentElement.getAttribute('data-tab-hash') : '';
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        super.bindEvents();
        Event.once('BV.dependency.loaded', this.onDependencyLoaded, this);
    }

    /**
     * Handler for submissionSubmitted event,
     * triggered when a consumer submits a submission form.
     * @param {Object} eventData - Third-party provider event data.
     */
    onSubmissionSubmitted(eventData) {
        Event.emit('BV.submission.submitted', eventData);
    }

    /**
     * Bazaarvoice library callback
     * @param {Object} BV Bazaarvoice library instance
     */
    onBVCallback(BV) {
        const reviewsApp = BV.reviews || BV.swat_reviews;
        const questionsApp = BV.questions || BV.swat_questions;

        reviewsApp.on('show', () => {
            if (this.reviewsTabHash) {
                if (!this.selectors.reviews.getAttribute('data-bv-show')) {
                    document.location.hash = '';
                    setHash('tab', this.reviewsTabHash);
                } else {
                    trigger('tab.change', this.selectors.reviewsTab, {
                        bubbles: true,
                    });
                }
            } else {
                document.location.hash = '';
                setHash('section', 'reviews');
            }
        });

        questionsApp.on('show', () => {
            if (this.questionsTabHash) {
                if (!this.selectors.reviews.getAttribute('data-bv-show')) {
                    document.location.hash = '';
                    setHash('tab', this.questionsTabHash);
                } else {
                    trigger('tab.change', this.selectors.questionsTab, {
                        bubbles: true,
                    });
                }
            }
        });
    }

    /**
     * Handler for submissionLoad event,
     * triggered when a consumer opens a review or Q&A submission form.
     * @param {Object} eventData - Third-party provider event data.
     */
    onSubmissionLoad(eventData) {
        Event.emit('BV.submission.load', eventData);
    }
}
